import { useEffect, useState } from 'react';

import { Link, useLocation } from 'react-router-dom';

import './giftPartnerGuide.css';
import content from './guide-content.json';
import spec from './openapi.json';

export const GIFT_PARTNER_GUIDE_PATH = '/gift-brand/api-integration/guide';
// 예제에 실제 API 주소를 그대로 보여준다 (지금 보고 있는 환경의 백엔드)
const API_BASE_URL_TOKEN = '{{API_BASE_URL}}';
const apiBaseUrl = (
  process.env.REACT_APP_BACKEND_DOMAIN || 'https://api.bugo.gaonprime.com'
).replace(/\/+$/, '');

export const GiftPartnerGuideResources = () => (
  <section className="partner-guide-resources" aria-label="API 개발 가이드">
    <div>
      <strong>연동 전에 개발 가이드를 확인하세요</strong>
      <p>
        인증 방법, API 명세, 배송 처리와 웹훅 예제를 개발 담당자에게 공유할 수 있습니다.
      </p>
    </div>
    <div className="partner-guide-links">
      <Link to={GIFT_PARTNER_GUIDE_PATH}>개발 가이드 보기 →</Link>
    </div>
  </section>
);

type Schema = {
  type?: string;
  format?: string;
  enum?: string[];
  default?: number;
  $ref?: string;
};
type ApiContent = { schema: Schema; example: unknown };
type Operation = {
  operationId: string;
  summary: string;
  description: string;
  parameters: {
    name: string;
    in: string;
    required: boolean;
    description: string;
    schema: Schema;
  }[];
  requestBody?: { content: { 'application/json': ApiContent } };
  responses: Record<
    string,
    // 이미지 응답·304는 JSON 본문이 없다
    { description: string; content?: { 'application/json'?: ApiContent } }
  >;
};
const endpoints = Object.entries(spec.paths).flatMap(([path, methods]) =>
  Object.entries(methods).map(([method, operation]) => ({
    path,
    method: method.toUpperCase(),
    operation: operation as Operation,
  })),
);

const CodeSample = ({ label, children }: { label: string; children: string }) => (
  <div className="partner-guide-code">
    <span>{label}</span>
    <pre tabIndex={0} aria-label={label}>
      <code>{children}</code>
    </pre>
  </div>
);

export const GiftPartnerGuidePage = () => {
  const [search, setSearch] = useState('');
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);
  const visibleEndpoints = endpoints.filter(({ path, method, operation }) =>
    `${path} ${method} ${operation.summary}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  return (
    <div className="partner-guide">
      <header className="partner-guide-header">
        <Link to="/gift-brand">답례품 배송관리자</Link>
        <Link to="/gift-brand/api-integration" className="partner-guide-cta">
          연동 신청·관리
        </Link>
      </header>
      <main>
        <div className="partner-guide-intro">
          <p className="partner-guide-eyebrow">DEVELOPER GUIDE · {content.version}</p>
          <h1>{content.title}</h1>
          <p>{content.description}</p>
          <p className="partner-guide-meta">
            로그인·연동 신청 없이 열람 가능 · 문서 기준일 {content.updatedAt}
          </p>
        </div>
        <div className="partner-guide-layout">
          <aside>
            <nav aria-label="가이드 목차">
              <a href="#start">연동 시작하기</a>
              <a href="#auth">인증과 호출 환경</a>
              <a href="#api">API 명세 · {endpoints.length}개</a>
              {content.sections.slice(2).map((section) => (
                <a key={section.id} href={`#${section.id}`}>
                  {section.title}
                </a>
              ))}
            </nav>
          </aside>
          <div className="partner-guide-body">
            {content.sections.map((section, index) => (
              <div key={section.id}>
                {index === 2 && (
                  <section id="api" className="partner-guide-section">
                    <h2>API 명세</h2>
                    <p>
                      공급사 서버에서 호출하는 API입니다. 모든 요청에 x-api-key 인증과
                      허용 IP 확인이 적용됩니다.
                    </p>
                    <p>
                      각 항목을 펼쳐 요청·응답을 확인하세요. 전체 필드 스키마는 OpenAPI
                      명세를 Swagger Editor 또는 Postman에 가져와 확인할 수 있습니다.
                    </p>
                    <label htmlFor="partner-api-search">API 검색</label>
                    <input
                      id="partner-api-search"
                      type="search"
                      placeholder="경로, 메서드, 기능 검색"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                    <p className="partner-guide-meta" role="status">
                      {visibleEndpoints.length}개 API
                    </p>
                    {visibleEndpoints.map(({ path, method, operation }) => {
                      const success = Object.entries(operation.responses).find(
                        ([status]) => status.startsWith('2'),
                      );
                      const body = operation.requestBody?.content['application/json'];
                      const requestPath = path.replace('{fullCode}', 'K3F9QZ7A-2');
                      const curl = `curl -X ${method} "${apiBaseUrl}${requestPath}" \\\n  -H "x-api-key: $GIFT_PARTNER_API_KEY"${
                        body
                          ? ` \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(
                              body.example,
                            )}'`
                          : ''
                      }${path.endsWith('/image') ? ' \\\n  -o thanks-card.png' : ''}`;
                      return (
                        <details
                          key={operation.operationId}
                          className="partner-guide-endpoint"
                        >
                          <summary>
                            <span
                              className={`partner-guide-method partner-guide-method-${method.toLowerCase()}`}
                            >
                              {method}
                            </span>
                            <code>{path}</code>
                            <span>{operation.summary}</span>
                          </summary>
                          <div className="partner-guide-endpoint-body">
                            <p>{operation.description}</p>
                            {!!operation.parameters.length && (
                              <div className="partner-guide-table-wrap">
                                <table>
                                  <caption>요청 파라미터</caption>
                                  <thead>
                                    <tr>
                                      <th>이름</th>
                                      <th>위치 / 타입</th>
                                      <th>설명</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {operation.parameters.map((parameter) => (
                                      <tr key={parameter.name}>
                                        <td>
                                          <code>{parameter.name}</code>
                                          {parameter.required && <span> (필수)</span>}
                                        </td>
                                        <td>
                                          {parameter.in} / {parameter.schema.type}
                                        </td>
                                        <td>{parameter.description}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {body && (
                              <CodeSample label="요청 본문 · status 필수, carrier·trackingNumber 선택 (각 100자 이내)">
                                {JSON.stringify(body.example, null, 2)}
                              </CodeSample>
                            )}
                            <CodeSample label="요청 예제 · 서버 환경변수 설정 후 실행">
                              {curl}
                            </CodeSample>
                            {success &&
                              (success[1].content?.['application/json'] ? (
                                <CodeSample label={`응답 예제 · HTTP ${success[0]}`}>
                                  {JSON.stringify(
                                    success[1].content['application/json'].example,
                                    null,
                                    2,
                                  )}
                                </CodeSample>
                              ) : (
                                <p className="partner-guide-meta">
                                  응답 · HTTP {success[0]}: {success[1].description}
                                </p>
                              ))}
                            <p className="partner-guide-meta">
                              오류:{' '}
                              {Object.entries(operation.responses)
                                .filter(([status]) => Number(status) >= 400)
                                .map(
                                  ([status, response]) =>
                                    `${status} ${response.description}`,
                                )
                                .join(' / ')}
                            </p>
                          </div>
                        </details>
                      );
                    })}
                    {!visibleEndpoints.length && (
                      <p>
                        검색 결과가 없습니다. 다른 경로나 기능 이름으로 검색해 주세요.
                      </p>
                    )}
                  </section>
                )}
                <section id={section.id} className="partner-guide-section">
                  <h2>{section.title}</h2>
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  {!!section.steps.length && (
                    <ol>
                      {section.steps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  )}
                  {!!section.headers.length && (
                    <div className="partner-guide-table-wrap">
                      <table>
                        <caption className="sr-only">{section.title}</caption>
                        <thead>
                          <tr>
                            {section.headers.map((header) => (
                              <th key={header}>{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.rows.map((row) => (
                            <tr key={row[0]}>
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {section.code && (
                    <CodeSample label={section.language}>
                      {section.code.split(API_BASE_URL_TOKEN).join(apiBaseUrl)}
                    </CodeSample>
                  )}
                </section>
              </div>
            ))}
            <section id="manual" className="partner-guide-section">
              <h2>매뉴얼 파일</h2>
              <p>
                PDF 매뉴얼과 OpenAPI 3.0 명세 파일은 담당자가 직접 전달합니다. 필요하면
                배송관리자 화면의 문의하기로 요청하세요. · 문서 기준일 {content.updatedAt}
              </p>
              <Link to="/gift-brand/api-integration" className="partner-guide-cta">
                가이드 확인 후 연동 신청·관리
              </Link>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};
